import { useEffect, useRef, useState } from 'react';
import type { TimePeriod } from '../../domain/types.js';
import { WIZARD_QUESTION_IDS } from '../../constants.js';
import { suggestTimePeriod } from './fallbacks.js';
import { AiFillBlanksButton } from './groundingStep.js';
import { OptionalQuestionsToggle } from './optionalQuestions.js';
import type { StepBlankSummary } from './suggestionProvider.js';
import './createWorldSteps.css';

export const DEFAULT_PERIOD_PRESENT = 'Present day';

export interface PeriodValidationIssue {
  fieldId: string;
  message: string;
}

function isPunctuationOnly(s: string): boolean {
  return s.trim().length > 0 && /^[\p{P}\p{S}\s]+$/u.test(s.trim());
}

export function trimTimePeriod(period: TimePeriod): TimePeriod {
  const value = period.present?.trim() ?? period.period?.trim() ?? '';
  return {
    present: value,
    period: value,
    rationale: period.rationale?.trim() ?? '',
  };
}

export function defaultTimePeriod(): TimePeriod {
  return {
    present: DEFAULT_PERIOD_PRESENT,
    period: DEFAULT_PERIOD_PRESENT,
    rationale: '',
  };
}

export function periodValidationIssues(period: TimePeriod | null): PeriodValidationIssue[] {
  const p = period ?? { present: '', period: '', rationale: '' };
  const value = p.present?.trim() || p.period?.trim() || '';
  const issues: PeriodValidationIssue[] = [];

  if (!value) {
    issues.push({
      fieldId: WIZARD_QUESTION_IDS.periodPresent,
      message: 'Enter when this world takes place.',
    });
    return issues;
  }
  if (isPunctuationOnly(value)) {
    issues.push({
      fieldId: WIZARD_QUESTION_IDS.periodPresent,
      message: 'Period must not be punctuation-only.',
    });
  }
  return issues;
}

export function validateTimePeriod(period: TimePeriod | null): string | null {
  return periodValidationIssues(period)[0]?.message ?? null;
}

export function firstIncompletePeriodField(period: TimePeriod | null): string | null {
  return periodValidationIssues(period)[0]?.fieldId ?? null;
}

function periodBlankSummary(period: TimePeriod | null): StepBlankSummary {
  const issues = periodValidationIssues(period);
  const labels = issues.map((issue) => ({
    fieldId: issue.fieldId,
    label: 'When does this world take place?',
  }));
  if (!period?.rationale?.trim()) {
    labels.push({ fieldId: WIZARD_QUESTION_IDS.periodRationale, label: 'Talk about this period' });
  }
  return { count: labels.length, labels };
}

function fillPeriodBlanks(period: TimePeriod | null, draftId: string): TimePeriod {
  const current = period ?? defaultTimePeriod();
  const suggestion = suggestTimePeriod(draftId);
  const value = current.present?.trim() || current.period?.trim() || suggestion.present || DEFAULT_PERIOD_PRESENT;
  return trimTimePeriod({
    present: value,
    period: value,
    rationale: current.rationale?.trim() ? current.rationale : (suggestion.rationale ?? ''),
  });
}

export function TimePeriodStepInit(props: {
  period: TimePeriod | null;
  onInit: (period: TimePeriod) => void;
  children: (period: TimePeriod) => JSX.Element;
}): JSX.Element {
  const period = props.period ?? defaultTimePeriod();
  useEffect(() => {
    if (!props.period) props.onInit(defaultTimePeriod());
  }, [props.period, props.onInit]);
  return props.children(period);
}

export function TimePeriodStep(props: {
  period: TimePeriod | null;
  draftId: string;
  onChange: (p: TimePeriod) => void;
  focusFieldId?: string | null;
  optionalOpen?: boolean;
  onOptionalOpenChange?: (open: boolean) => void;
}): JSX.Element {
  const p = props.period ?? defaultTimePeriod();
  const firstInvalid = props.focusFieldId ?? firstIncompletePeriodField(props.period);
  const presentRef = useRef<HTMLInputElement>(null);
  const rationaleRef = useRef<HTMLTextAreaElement>(null);
  const [optionalOpenLocal, setOptionalOpenLocal] = useState(false);
  const optionalOpen = props.optionalOpen ?? optionalOpenLocal;
  const setOptionalOpen = props.onOptionalOpenChange ?? setOptionalOpenLocal;

  useEffect(() => {
    if (!props.focusFieldId) return;
    if (props.focusFieldId === WIZARD_QUESTION_IDS.periodPresent) presentRef.current?.focus();
    if (props.focusFieldId === WIZARD_QUESTION_IDS.periodRationale) {
      setOptionalOpen(true);
      rationaleRef.current?.focus();
    }
  }, [props.focusFieldId, setOptionalOpen]);

  const blankSummary = periodBlankSummary(props.period);

  function handleAiFill(): { summary: string; nothingToFill: boolean; filledLabels: string[] } {
    if (blankSummary.count === 0) {
      return { summary: 'Nothing to fill in', nothingToFill: true, filledLabels: [] };
    }
    const next = fillPeriodBlanks(props.period, props.draftId);
    if (!props.period?.rationale?.trim()) setOptionalOpen(true);
    props.onChange(next);
    const filledLabels = blankSummary.labels.map((l) => l.label);
    return {
      summary: `Filled ${filledLabels.length} blank${filledLabels.length === 1 ? '' : 's'}: ${filledLabels.join(', ')}`,
      nothingToFill: false,
      filledLabels,
    };
  }

  return (
    <div className="world-form">
      <div
        className={`world-field-row ${firstInvalid === WIZARD_QUESTION_IDS.periodPresent ? 'world-field-invalid' : ''}`}
      >
        <label htmlFor="period-present">When does this world take place?</label>
        <input
          id="period-present"
          ref={presentRef}
          value={p.present ?? ''}
          onChange={(e) => {
            const next = e.target.value;
            props.onChange({ ...p, present: next, period: next });
          }}
          aria-invalid={firstInvalid === WIZARD_QUESTION_IDS.periodPresent}
        />
      </div>

      <OptionalQuestionsToggle open={optionalOpen} onToggle={() => setOptionalOpen(!optionalOpen)} />
      {optionalOpen && (
        <div
          className={`world-field-row world-optional-section ${props.focusFieldId === WIZARD_QUESTION_IDS.periodRationale ? 'world-field-invalid' : ''}`}
        >
          <label htmlFor="period-rationale">Talk about this period, why did you choose it (optional)?</label>
          <textarea
            id="period-rationale"
            ref={rationaleRef}
            rows={3}
            value={p.rationale ?? ''}
            onChange={(e) => props.onChange({ ...p, rationale: e.target.value })}
          />
        </div>
      )}

      <AiFillBlanksButton summary={blankSummary} onConfirmFill={handleAiFill} />
    </div>
  );
}
